/**
 * Policy Blast-Radius Replay — retroactive old-vs-proposed diff engine.
 *
 * Raising autonomy is only safe when the admin can see EXACTLY which of the
 * last N real proposals would have been routed differently under the
 * proposed policy. This module replays historical proposals through both
 * the old and the proposed policy and buckets every transition:
 *
 *   • nowAutoApprove — was require-approval, would now auto-fire (the
 *     dangerous direction; gates Save),
 *   • newlyBlocked   — was auto-approve, would now require approval,
 *   • unchanged      — same outcome under both policies.
 *
 * Honesty rules:
 *   • Classification is delegated to an injected `decide` function so this
 *     engine stays decoupled from any concrete policy shape and can never
 *     fork the shipped threshold logic (see autonomyPolicy.evaluateAction).
 *   • Pure and IO-free: proposals and policies are passed in as data.
 *   • Replay never mutates anything — it is a pure projection.
 */

/** Where a proposal lands under a policy. */
export type TriageOutcome = 'auto-approve' | 'require-approval';

/** One historical proposal replayed through old and proposed policy. */
export type ReplayedProposal = {
  proposalId: string;
  actionType: string;
  oldOutcome: TriageOutcome;
  newOutcome: TriageOutcome;
};

/** Full blast radius of a policy change over a replay window. */
export type PolicyDelta = {
  /** require-approval → auto-approve: proposals that would newly auto-fire. */
  nowAutoApprove: ReplayedProposal[];
  /** auto-approve → require-approval: proposals newly held for a human. */
  newlyBlocked: ReplayedProposal[];
  /** Count of proposals whose outcome is identical under both policies. */
  unchanged: number;
  /** Total proposals replayed. Always nowAutoApprove + newlyBlocked + unchanged. */
  total: number;
};

/**
 * Route one proposal through a policy via the injected `decide` function.
 * A thin, honest wrapper — the engine never re-implements thresholds.
 */
export function evaluateOutcome(
  proposal: { actionType: string; confidence?: number; valueAtRisk?: number; [k: string]: unknown },
  policy: unknown,
  decide: (proposal: any, policy: any) => TriageOutcome,
): TriageOutcome {
  return decide(proposal, policy);
}

/**
 * Replay every proposal through both policies and bucket the transitions.
 * An empty window is honestly an all-zero delta.
 */
export function computeBlastRadius(
  proposals: any[],
  oldPolicy: unknown,
  newPolicy: unknown,
  decide: (proposal: any, policy: any) => TriageOutcome,
): PolicyDelta {
  const nowAutoApprove: ReplayedProposal[] = [];
  const newlyBlocked: ReplayedProposal[] = [];
  let unchanged = 0;

  for (const proposal of proposals) {
    const oldOutcome = evaluateOutcome(proposal, oldPolicy, decide);
    const newOutcome = evaluateOutcome(proposal, newPolicy, decide);
    const replayed: ReplayedProposal = {
      proposalId: String(proposal?.proposalId ?? proposal?.id ?? ''),
      actionType: String(proposal?.actionType ?? ''),
      oldOutcome,
      newOutcome,
    };
    if (oldOutcome === 'require-approval' && newOutcome === 'auto-approve') {
      nowAutoApprove.push(replayed);
    } else if (oldOutcome === 'auto-approve' && newOutcome === 'require-approval') {
      newlyBlocked.push(replayed);
    } else {
      unchanged += 1;
    }
  }

  return { nowAutoApprove, newlyBlocked, unchanged, total: proposals.length };
}

/** Human-readable one-liner for the confirm dialog / save banner. */
export function blastRadiusSummary(delta: PolicyDelta): string {
  return `${delta.nowAutoApprove.length} would now auto-approve, ${delta.newlyBlocked.length} newly blocked`;
}

/**
 * Pure save gate: a policy change that promotes ANY historical proposal to
 * auto-approve is not savable without explicit admin confirmation. Callers
 * clear the gate by collecting that confirmation in the UI.
 */
export function isSaveBlocked(delta: PolicyDelta): boolean {
  return delta.nowAutoApprove.length > 0;
}
